import type TicketStore from 'customer-flows/stores/tickets'
import uid from 'customer-flows/utils/uid'

export default function customQuestion({stores}: CommandArgs) {
  const customQuestionStore: CustomQuestionStore = stores['custom-question']
  const ticketStore: TicketStore = stores['tickets']

  /**
   * initializes questions for the 1st time we render a custom-question form
   * @param questionConfigs
   */
  function initializeQuestionAnswers(questionConfigs: QuestionActivityConfiguration[] = []): void {
    questionConfigs.forEach(questionConfig => {
      if (questionConfig.question.answerDefaultValue) {
        const foundAnswer = customQuestionStore.getQuestionAnswerById(questionConfig.question.id)

        if (!foundAnswer) {
          customQuestionStore.perBookingQuestionAnswers.push(
            buildQuestionAnswer(questionConfig, questionConfig.question.answerDefaultValue)
          )
        }

        customQuestionStore.perBookingQuestionAnswers = [...customQuestionStore.perBookingQuestionAnswers]
      }
    })
  }

  /**
   * initializes a Map of per-guest answers, mapped by ticket.refId
   * @param tickets
   * @param questionConfigs
   */
  function initializePerGuestQuestionAnswers(
    tickets: SelectedTicket[],
    questionConfigs: QuestionActivityConfiguration[] = []
  ): void {
    tickets.forEach(ticket => {
      questionConfigs.forEach(questionConfig => {
        if (questionConfig.question.answerDefaultValue) {
          const foundAnswer = customQuestionStore.getPerGuestQuestionAnswersById(ticket.refId, questionConfig.question.id)

          if (!foundAnswer) {
            const answersForTicket = customQuestionStore.perGuestQuestionAnswers.get(ticket.refId) ?? []
            answersForTicket.push(buildQuestionAnswer(questionConfig, questionConfig.question.answerDefaultValue))
            customQuestionStore.perGuestQuestionAnswers.set(ticket.refId, answersForTicket)
          }

          customQuestionStore.perGuestQuestionAnswers = new Map(customQuestionStore.perGuestQuestionAnswers)
        }
      })
    })
  }

  /**
   * Upserts a question answer
   * @param questionConfig
   * @param answer
   */
  function upsertQuestionAnswer(questionConfig: QuestionActivityConfiguration, answer?: string | boolean | CustomQuestionOption) {
    const foundAnswer = customQuestionStore.getQuestionAnswerById(questionConfig.question.id)

    if (foundAnswer) {
      overrideAnswer(questionConfig, foundAnswer, answer)
    } else {
      customQuestionStore.perBookingQuestionAnswers.push(buildQuestionAnswer(questionConfig, answer))
    }

    customQuestionStore.perBookingQuestionAnswers = [...customQuestionStore.perBookingQuestionAnswers]
  }

  function upsertPerGuestQuestionAnswer(
    ticketRefId: string,
    questionConfig: QuestionActivityConfiguration,
    answer?: string | boolean | CustomQuestionOption
  ) {
    const foundAnswer = customQuestionStore.getPerGuestQuestionAnswersById(ticketRefId, questionConfig.question.id)

    if (foundAnswer) {
      overrideAnswer(questionConfig, foundAnswer, answer)
    } else {
      const answersForTicket = customQuestionStore.perGuestQuestionAnswers.get(ticketRefId) ?? []
      answersForTicket.push(buildQuestionAnswer(questionConfig, answer))
      customQuestionStore.perGuestQuestionAnswers.set(ticketRefId, answersForTicket)
    }

    customQuestionStore.perGuestQuestionAnswers = new Map(customQuestionStore.perGuestQuestionAnswers)
  }

  /**
   * removes all related answers to a given ticketRefId
   * cancels any items in the answers so BE knows to remove them
   * @param ticketRefId
   */
  function removePerGuestQuestionAnswersForTicket(ticketRefId: string) {
    const ticketAnswers = customQuestionStore.perGuestQuestionAnswers.get(ticketRefId) ?? []

    // cancel items and mark answers as deleted so BE knows to remove them
    ticketAnswers.forEach(answer => {
      answer.deleted = true
      if (answer.items && answer.items.length > 0) {
        answer.items.forEach(cancelItem)
        answer.items = [...answer.items]
      }
    })

    // keep the answers in the map (marked as deleted) so they get sent to BE
    customQuestionStore.perGuestQuestionAnswers.set(ticketRefId, ticketAnswers)
    customQuestionStore.perGuestQuestionAnswers = new Map(customQuestionStore.perGuestQuestionAnswers)
  }

  /**
   * Transforms a question configuration and answer into a quote answer input
   * @param config
   * @param answer
   */
  function buildQuestionAnswer(
    config: QuestionActivityConfiguration,
    answer?: string | boolean | CustomQuestionOption
  ): QuoteSparseQuestionAnswerInput {
    const base: QuoteSparseQuestionAnswerInput = {
      refid: uid(),
      questionId: config.question.id
    }

    if (config.question.type === 'TEXT') {
      return {
        ...base,
        questionAnswerText: String(answer)
      }
    } else if (config.question.type === 'CHECK_BOX') {
      return {
        ...base,
        isChecked: Boolean(answer),
        questionAnswerText: answer ? 'Yes' : 'No'
      }
    } else if (config.question.type === 'SELECT_ONE') {
      const answerOption: CustomQuestionOption | string | boolean | undefined = answer

      // as defaultAnswers come as a simple text value instead of an option ID,
      // we're obligated to find the right option matching texts
      const selectedOption = answerOption
        ? config.question.options?.find(
          option => option.id === (answerOption as CustomQuestionOption)?.id || option.text === answerOption
        )
        : undefined

      if (!selectedOption) {
        return base
      }

      const result: QuoteSparseQuestionAnswerInput = {
        ...base,
        questionAnswerOptionId: selectedOption.id,
        questionAnswerText: selectedOption.text
      }

      // if the selected option has an itemOption, add it to the items array
      if (selectedOption.itemOption?.item?.id) {
        // per-guest questions: always create 1 item (each ticket gets its own answer, so chargePerGuest is ignored)
        // per-booking questions: create multiple items only if chargePerGuest is true
        let itemCount = 1

        if (!config.question.perGuest && selectedOption.chargePerGuest) {
          const ticketCount = ticketStore.selectedTicketNames.length
          itemCount = ticketCount
        }

        const itemId = selectedOption.itemOption.item.id
        const itemOptionId = selectedOption.itemOption.id
        result.items = Array.from({ length: itemCount }, () => createConfirmedItem(itemId, itemOptionId))
      }

      return result
    } else {
      return {
        ...base,
        questionAnswerText: String(answer)
      }
    }
  }

  /**
   * overrides the answer on an existing answer object
   * @param questionConfig
   * @param existingAnswer
   * @param answer
   */
  function overrideAnswer(
    questionConfig: QuestionActivityConfiguration,
    existingAnswer: QuoteSparseQuestionAnswerInput,
    answer?: string | boolean | CustomQuestionOption
  ) {
    if (!questionConfig) return

    if (questionConfig.question.type === 'TEXT') {
      existingAnswer.questionAnswerText = String(answer)
    } else if (questionConfig.question.type === 'CHECK_BOX') {
      existingAnswer.isChecked = Boolean(answer)
      existingAnswer.questionAnswerText = answer ? 'Yes' : 'No'
    } else if (questionConfig.question.type === 'SELECT_ONE') {
      const answerOption = answer as CustomQuestionOption

      const selectedOption = questionConfig.question.options?.find(
        option => option.id === answerOption.id || option.text === answerOption.text
      )

      if (selectedOption) {
        existingAnswer.questionAnswerOptionId = selectedOption.id
        existingAnswer.questionAnswerText = selectedOption.text

        // if the selected option has an itemOption, add it to the items array
        // else if switching to an option without an itemOption, cancel current items
        if (selectedOption.itemOption?.item?.id) {
          const itemOption = selectedOption.itemOption
          const itemId = itemOption.item!.id
          const itemOptionId = itemOption.id

          // per-guest questions: always create 1 item (chargePerGuest is ignored)
          // per-booking questions: create multiple items only if chargePerGuest is true
          let desiredCount = 1

          if (!questionConfig.question.perGuest && selectedOption.chargePerGuest) {
            const ticketCount = ticketStore.selectedTicketNames.length
            desiredCount = ticketCount
          }

          const currentConfirmedItems = existingAnswer.items?.filter(
            item => item.reservationStatus === 'CONFIRMED'
          ) ?? []

          // check if we're switching to a different item/option or just adjusting count
          // if item length matches, no changes are needed
          const firstItem = currentConfirmedItems[0]
          const isSameItemOption = firstItem &&
            firstItem.itemId === itemId &&
            firstItem.itemOptions?.[0]?.itemOptionId === itemOptionId

          if (isSameItemOption && currentConfirmedItems.length === desiredCount) {
            return
          }

          // cancel existing items before creating new ones (so BE knows to remove them)
          if (existingAnswer.items && existingAnswer.items.length > 0) {
            existingAnswer.items.forEach(cancelItem)
          }

          // create new items array, keeping canceled items for BE and adding new confirmed items
          const canceledItems = existingAnswer.items ?? []
          const newItems = Array.from({ length: desiredCount }, () => createConfirmedItem(itemId, itemOptionId))
          existingAnswer.items = [...canceledItems, ...newItems]
        } else if (existingAnswer.items && existingAnswer.items.length > 0) {
          existingAnswer.items.forEach(cancelItem)
          existingAnswer.items = [...existingAnswer.items]
        }
      }
    } else {
      existingAnswer.questionAnswerText = String(answer)
    }
  }

  /**
   * adds follow-up questions after the current question being answered
   * NOTE: this does NOT remove existing follow-up, call removeFollowUpQuestionsAfter first when needed
   * @param currentQuestionConfig
   * @param followupQuestions
   */
  function addFollowUpQuestions(
    currentQuestionConfig: QuestionActivityConfiguration,
    followupQuestions: CustomQuestion[]
  ): void {
    // find the index of the current question in the configurations array
    const currentIndex = customQuestionStore.questionActivityConfigurations.findIndex(
      config => config.id === currentQuestionConfig.id
    )

    if (currentIndex === -1) {
      return
    }

    // convert follow-up questions to QuestionActivityConfiguration format
    // inherit isRequired from parent question - if parent is required, follow-ups are too
    const followUpConfigs: QuestionActivityConfiguration[] = followupQuestions.map(question => ({
      id: question.id,
      isRequired: currentQuestionConfig.isRequired,
      question: question
    }))

    // track these questions as follow-ups of the current question
    followupQuestions.forEach(question => {
      registerFollowUpQuestion(question.id, currentQuestionConfig.question.id)
    })

    // insert the follow-up questions right after the current question
    customQuestionStore.questionActivityConfigurations.splice(
      currentIndex + 1,
      0,
      ...followUpConfigs
    )

    // trigger reactivity
    customQuestionStore.questionActivityConfigurations = [...customQuestionStore.questionActivityConfigurations]

    // initialize default values for the follow-up questions
    initializeQuestionAnswers(followUpConfigs)

    // recursively check if any of the follow-up questions have default values that trigger more follow-ups
    followUpConfigs.forEach(followUpConfig => {
      if (followUpConfig.question.type === 'SELECT_ONE' && followUpConfig.question.answerDefaultValue) {
        // find the option that matches the default value
        const selectedOption = followUpConfig.question.options?.find(
          option => option.text === followUpConfig.question.answerDefaultValue || option.id === followUpConfig.question.answerDefaultValue
        )

        // if the selected option has follow-up questions, recursively add them
        if (selectedOption?.followupQuestions && selectedOption.followupQuestions.length > 0) {
          addFollowUpQuestions(followUpConfig, selectedOption.followupQuestions)
        }
      }
    })
  }

  /**
   * adds follow-up questions for a per-guest question for a specific ticket
   * @param currentQuestionConfig
   * @param followupQuestions
   * @param ticketRefId
   */
  function addPerGuestFollowUpQuestions(
    currentQuestionConfig: QuestionActivityConfiguration,
    followupQuestions: CustomQuestion[],
    ticketRefId: string
  ): void {
    // first, add the follow-up questions to the global configurations if not already there
    const existingFollowUpIds = customQuestionStore.questionActivityConfigurations.map(config => config.question.id)
    const newFollowUpConfigs: QuestionActivityConfiguration[] = followupQuestions
      .filter(question => !existingFollowUpIds.includes(question.id))
      .map(question => ({
        id: question.id,
        isRequired: currentQuestionConfig.isRequired,
        question: question
      }))

    if (newFollowUpConfigs.length > 0) {
      const currentIndex = customQuestionStore.questionActivityConfigurations.findIndex(
        config => config.id === currentQuestionConfig.id
      )

      // insert the follow-up questions right after the current question
      if (currentIndex !== -1) {
        customQuestionStore.questionActivityConfigurations.splice(
          currentIndex + 1,
          0,
          ...newFollowUpConfigs
        )

        customQuestionStore.questionActivityConfigurations = [...customQuestionStore.questionActivityConfigurations]
      }
    }

    // track these questions as follow-ups of the current question
    followupQuestions.forEach(question => {
      registerFollowUpQuestion(question.id, currentQuestionConfig.question.id)
    })

    // track which follow-up questions should be visible for this specific ticket
    const currentFollowUps = customQuestionStore.perGuestFollowUpQuestions.get(ticketRefId) ?? []
    const newFollowUpIds = followupQuestions.map(q => q.id)
    const updatedFollowUps = [...new Set([...currentFollowUps, ...newFollowUpIds])]

    customQuestionStore.perGuestFollowUpQuestions.set(ticketRefId, updatedFollowUps)
    customQuestionStore.perGuestFollowUpQuestions = new Map(customQuestionStore.perGuestFollowUpQuestions)

    // initialize default values for the follow-up questions
    // get all follow-up configs (both new and existing) for this ticket
    const allFollowUpConfigs = followupQuestions.map(question => {
      return customQuestionStore.questionActivityConfigurations.find(config => config.question.id === question.id)
    }).filter(Boolean) as QuestionActivityConfiguration[]

    // determine which tickets to initialize based on whether the question is per-guest
    const ticketsToInitialize = allFollowUpConfigs.some(config => config.question.perGuest)
      ? [ticketStore.selectedTicketNames.find(t => t.refId === ticketRefId)].filter(Boolean) as SelectedTicket[]
      : []

    // initialize per-guest questions for this specific ticket
    if (ticketsToInitialize.length > 0) {
      const perGuestConfigs = allFollowUpConfigs.filter(config => config.question.perGuest)
      initializePerGuestQuestionAnswers(ticketsToInitialize, perGuestConfigs)
    }

    // initialize per-booking questions (these apply to all tickets)
    const perBookingConfigs = allFollowUpConfigs.filter(config => !config.question.perGuest)
    initializeQuestionAnswers(perBookingConfigs)

    // recursively check if any of the follow-up questions have default values that trigger more follow-ups
    allFollowUpConfigs.forEach(followUpConfig => {
      if (followUpConfig.question.type === 'SELECT_ONE' && followUpConfig.question.answerDefaultValue) {
        // find the option that matches the default value
        const selectedOption = followUpConfig.question.options?.find(
          option => option.text === followUpConfig.question.answerDefaultValue || option.id === followUpConfig.question.answerDefaultValue
        )

        // if the selected option has follow-up questions, recursively add them
        if (selectedOption?.followupQuestions && selectedOption.followupQuestions.length > 0) {
          // determine if we should use per-guest or per-booking recursion based on the follow-up config
          if (followUpConfig.question.perGuest) {
            addPerGuestFollowUpQuestions(followUpConfig, selectedOption.followupQuestions, ticketRefId)
          } else {
            addFollowUpQuestions(followUpConfig, selectedOption.followupQuestions)
          }
        }
      }
    })
  }

  /**
   * registers a question as a follow-up of a parent question
   * @param followUpQuestionId
   * @param parentQuestionId
   */
  function registerFollowUpQuestion(followUpQuestionId: string, parentQuestionId: string): void {
    customQuestionStore.followUpQuestionParentMap.set(followUpQuestionId, parentQuestionId)
    customQuestionStore.followUpQuestionParentMap = new Map(customQuestionStore.followUpQuestionParentMap)
  }

  /**
   * removes follow-up questions that were added after a specific question
   * recursively removes nested follow-ups as well
   * @param parentQuestionId
   */
  function removeFollowUpQuestionsAfter(parentQuestionId: string): void {
    const questionsToRemove = new Set<string>()

    function collectDescendants(questionId: string) {
      customQuestionStore.followUpQuestionParentMap.forEach((parent, followUpId) => {
        if (parent === questionId) {
          questionsToRemove.add(followUpId)
          collectDescendants(followUpId)
        }
      })
    }

    collectDescendants(parentQuestionId)

    customQuestionStore.questionActivityConfigurations = customQuestionStore.questionActivityConfigurations.filter(
      config => !questionsToRemove.has(config.question.id)
    )

    questionsToRemove.forEach(questionId => {
      customQuestionStore.followUpQuestionParentMap.delete(questionId)
    })
    customQuestionStore.followUpQuestionParentMap = new Map(customQuestionStore.followUpQuestionParentMap)

    // mark any existing answers for these questions as deleted (so BE knows to cancel them)
    customQuestionStore.perBookingQuestionAnswers.forEach(answer => {
      prepareQuestionForDelete(answer, questionsToRemove)
    })

    customQuestionStore.perBookingQuestionAnswers = [...customQuestionStore.perBookingQuestionAnswers]
  }

  /**
   * removes per-guest follow-up questions for a specific ticket
   * @param parentQuestionId
   * @param ticketRefId
   */
  function removePerGuestFollowUpQuestionsAfter(parentQuestionId: string, ticketRefId: string): void {
    const questionsToRemove = new Set<string>()

    function collectDescendants(questionId: string) {
      customQuestionStore.followUpQuestionParentMap.forEach((parent, followUpId) => {
        if (parent === questionId) {
          questionsToRemove.add(followUpId)
          collectDescendants(followUpId)
        }
      })
    }

    collectDescendants(parentQuestionId)

    // remove these follow-up questions from this ticket's list
    const currentFollowUps = customQuestionStore.perGuestFollowUpQuestions.get(ticketRefId) ?? []
    const updatedFollowUps = currentFollowUps.filter(questionId => !questionsToRemove.has(questionId))

    if (updatedFollowUps.length > 0) {
      customQuestionStore.perGuestFollowUpQuestions.set(ticketRefId, updatedFollowUps)
    } else {
      customQuestionStore.perGuestFollowUpQuestions.delete(ticketRefId)
    }

    customQuestionStore.perGuestFollowUpQuestions = new Map(customQuestionStore.perGuestFollowUpQuestions)

    // mark any existing answers for each ticket's questions as deleted (so BE knows to cancel them)
    const ticketAnswers = customQuestionStore.perGuestQuestionAnswers.get(ticketRefId) ?? []

    ticketAnswers.forEach(answer => {
      prepareQuestionForDelete(answer, questionsToRemove)
    })

    customQuestionStore.perGuestQuestionAnswers.set(ticketRefId, ticketAnswers)
    customQuestionStore.perGuestQuestionAnswers = new Map(customQuestionStore.perGuestQuestionAnswers)
  }

  /**
   * marks a question as ready to delete for the BE
   * @param answer
   * @param questionsToRemove
   */
  function prepareQuestionForDelete(answer: QuoteSparseQuestionAnswerInput, questionsToRemove: Set<string>) {
    if (!answer.questionId || !questionsToRemove.has(answer.questionId)) {
      return
    }

    answer.deleted = true

    // cancel any items in the answer
    if (answer.items && answer.items.length > 0) {
      answer.items.forEach(cancelItem)
      answer.items = [...answer.items]
    }
  }

  /**
   * removes answers that are marked as deleted from the store
   * we use this to clean up our UI after BE has the deleted answers
   */
  function cleanupDeletedAnswers(): void {
    // remove deleted answers from per-booking questions
    customQuestionStore.perBookingQuestionAnswers = customQuestionStore.perBookingQuestionAnswers.filter(
      answer => !answer.deleted
    )

    // remove deleted answers from per-guest questions
    customQuestionStore.perGuestQuestionAnswers.forEach((answers, ticketRefId) => {
      const filteredAnswers = answers.filter(answer => !answer.deleted)
      customQuestionStore.perGuestQuestionAnswers.set(ticketRefId, filteredAnswers)
    })

    customQuestionStore.perGuestQuestionAnswers = new Map(customQuestionStore.perGuestQuestionAnswers)
  }

  /**
   * clears all follow-up question tracking
   * this should be called when entering a new page to reset follow-up state
   */
  function clearFollowUpTracking(): void {
    const followUpQuestionIds = Array.from(customQuestionStore.followUpQuestionParentMap.keys())

    // remove follow-up questions
    if (followUpQuestionIds.length > 0) {
      customQuestionStore.questionActivityConfigurations = customQuestionStore.questionActivityConfigurations.filter(
        config => !followUpQuestionIds.includes(config.question.id)
      )
    }

    customQuestionStore.followUpQuestionParentMap = new Map()
    customQuestionStore.perGuestFollowUpQuestions = new Map()
  }

  /**
   * clears all question answers (both per-guest and per-booking)
   * this should be called on the confirmation page to ensure fresh answers
   * note: this marks answers as deleted and cancels items so BE stays in sync
   */
  function clearAllAnswers(): void {
    // cancel items and mark per-guest answers as deleted
    customQuestionStore.perGuestQuestionAnswers.forEach((answers, ticketRefId) => {
      answers.forEach(answer => {
        answer.deleted = true
        if (answer.items && answer.items.length > 0) {
          answer.items.forEach(cancelItem)
          answer.items = [...answer.items]
        }
      })
      customQuestionStore.perGuestQuestionAnswers.set(ticketRefId, answers)
    })

    customQuestionStore.perGuestQuestionAnswers = new Map(customQuestionStore.perGuestQuestionAnswers)

    // cancel items and mark per-booking answers as deleted
    customQuestionStore.perBookingQuestionAnswers.forEach(answer => {
      answer.deleted = true
      if (answer.items && answer.items.length > 0) {
        answer.items.forEach(cancelItem)
        answer.items = [...answer.items]
      }
    })

    customQuestionStore.perBookingQuestionAnswers = [...customQuestionStore.perBookingQuestionAnswers]
  }

  /**
   * initializes follow-up questions based on existing answers or default values
   * @param tickets - selected tickets for per-guest question initialization
   */
  function initializeFollowUpQuestionsFromDefaults(tickets: SelectedTicket[] = []): void {
    // process per-booking questions recursively
    processPerBookingFollowUps()

    function processPerBookingFollowUps(): void {
      let hasAddedNewFollowUps = false

      customQuestionStore.perBookingQuestionConfigs.forEach(questionConfig => {
        if (questionConfig.question.type !== 'SELECT_ONE') {
          return
        }

        const existingAnswer = customQuestionStore.getQuestionAnswerById(questionConfig.question.id)
        let selectedOption: CustomQuestionOption | undefined

        // find the option that matches the existing answer
        // otherwise, find the option that matches the default value
        if (existingAnswer?.questionAnswerOptionId) {
          selectedOption = questionConfig.question.options?.find(
            option => option.id === existingAnswer.questionAnswerOptionId
          )
        } else if (questionConfig.question.answerDefaultValue) {
          selectedOption = questionConfig.question.options?.find(
            option => option.text === questionConfig.question.answerDefaultValue || option.id === questionConfig.question.answerDefaultValue
          )
        }

        // if we found a selected option with follow-up questions, add them
        if (selectedOption?.followupQuestions && selectedOption.followupQuestions.length > 0) {
          // check if these follow-ups are already added
          const alreadyAdded = selectedOption.followupQuestions.every(fq =>
            customQuestionStore.questionActivityConfigurations.some(config => config.question.id === fq.id)
          )

          if (!alreadyAdded) {
            addFollowUpQuestions(questionConfig, selectedOption.followupQuestions)
            hasAddedNewFollowUps = true

            // initialize answers for the follow-up questions that have default values
            const followUpConfigs = selectedOption.followupQuestions.map(question => {
              return customQuestionStore.questionActivityConfigurations.find(config => config.question.id === question.id)
            }).filter(Boolean) as QuestionActivityConfiguration[]

            initializeQuestionAnswers(followUpConfigs)
          }
        }
      })

      // if we added new follow-ups, recursively process them
      if (hasAddedNewFollowUps) {
        processPerBookingFollowUps()
      }
    }

    // check per-guest questions for existing answers or default answers that have follow-ups
    // ONLY check questions on the current page - do NOT reuse answers from other pages
    tickets.forEach(ticket => {
      processPerGuestFollowUps(ticket)
    })

    function processPerGuestFollowUps(ticket: SelectedTicket): void {
      let hasAddedNewFollowUps = false

      customQuestionStore.perGuestQuestionConfigs.forEach(questionConfig => {
        if (questionConfig.question.type !== 'SELECT_ONE') {
          return
        }

        const existingAnswer = customQuestionStore.getPerGuestQuestionAnswersById(ticket.refId, questionConfig.question.id)
        let selectedOption: CustomQuestionOption | undefined

        // find the option that matches the existing answer
        // otherwise, find the option that matches the default value
        if (existingAnswer?.questionAnswerOptionId) {
          selectedOption = questionConfig.question.options?.find(
            option => option.id === existingAnswer.questionAnswerOptionId
          )
        } else if (questionConfig.question.answerDefaultValue) {
          selectedOption = questionConfig.question.options?.find(
            option => option.text === questionConfig.question.answerDefaultValue || option.id === questionConfig.question.answerDefaultValue
          )
        }

        // if we found a selected option with follow-up questions, add them for this specific ticket
        if (selectedOption?.followupQuestions && selectedOption.followupQuestions.length > 0) {
          // check if these follow-ups are already added for this ticket
          const ticketFollowUps = customQuestionStore.perGuestFollowUpQuestions.get(ticket.refId) ?? []
          const alreadyAdded = selectedOption.followupQuestions.every(fq => ticketFollowUps.includes(fq.id))

          if (!alreadyAdded) {
            addPerGuestFollowUpQuestions(questionConfig, selectedOption.followupQuestions, ticket.refId)
            hasAddedNewFollowUps = true

            // initialize answers for the follow-up questions that have default values
            const followUpConfigs = selectedOption.followupQuestions.map(question => {
              return customQuestionStore.questionActivityConfigurations.find(config => config.question.id === question.id)
            }).filter(Boolean) as QuestionActivityConfiguration[]

            initializePerGuestQuestionAnswers([ticket], followUpConfigs)
          }
        }
      })

      // if we added new follow-ups, recursively process them
      if (hasAddedNewFollowUps) {
        processPerGuestFollowUps(ticket)
      }
    }

  }

  /**
   * syncs per-booking question charges with current ticket count
   * this should be called when ticket count changes after questions are answered
   */
  function syncPerBookingChargesWithTicketCount(): void {
    const ticketCount = ticketStore.selectedTicketNames.length

    customQuestionStore.perBookingQuestionAnswers.forEach(answer => {
      if (!answer.items || answer.items.length === 0) {
        return
      }

      // find the question config for this answer
      const questionConfig = customQuestionStore.perBookingQuestionConfigs.find(
        config => config.question.id === answer.questionId
      )

      if (!questionConfig || questionConfig.question.type !== 'SELECT_ONE') {
        return
      }

      // find the selected option
      const selectedOption = questionConfig.question.options?.find(
        option => option.id === answer.questionAnswerOptionId
      )

      if (!selectedOption?.chargePerGuest || !selectedOption.itemOption?.item?.id) {
        return
      }

      const itemId = selectedOption.itemOption.item.id
      const itemOptionId = selectedOption.itemOption.id

      // adjust items count to match ticket count
      const currentConfirmedItems = answer.items.filter(
        item => item.reservationStatus === 'CONFIRMED'
      )

      // length matches, no need for adjustments
      // otherwise, add or cancel items as needed
      if (currentConfirmedItems.length === ticketCount) {
        return
      } else if (currentConfirmedItems.length < ticketCount) {
        const itemsToAdd = ticketCount - currentConfirmedItems.length

        for (let i = 0; i < itemsToAdd; i++) {
          answer.items.push(createConfirmedItem(itemId, itemOptionId))
        }
      } else {
        // need to cancel excess items
        const itemsToCancel = currentConfirmedItems.length - ticketCount
        let canceledCount = 0

        for (const item of answer.items) {
          if (canceledCount >= itemsToCancel) {
            break
          }

          if (item.reservationStatus === 'CONFIRMED') {
            cancelItem(item)
            canceledCount++
          }
        }
      }
    })

    customQuestionStore.perBookingQuestionAnswers = [...customQuestionStore.perBookingQuestionAnswers]
  }

  /**
   * creates a confirmed item with the given itemId and itemOptionId
   */
  function createConfirmedItem(itemId: string, itemOptionId: string): QuoteSparseItemInput {
    return {
      itemId,
      itemOptions: [{
        itemOptionId,
        refid: uid(),
        reservationStatus: 'CONFIRMED'
      }],
      refid: uid(),
      reservationStatus: 'CONFIRMED'
    }
  }

  /**
   * cancels an item by setting its reservationStatus to CANCELED
   */
  function cancelItem(item: QuoteSparseItemInput): void {
    item.reservationStatus = 'CANCELED'
    item.itemOptions?.forEach(itemOption => {
      itemOption.reservationStatus = 'CANCELED'
    })
  }

  return {
    initializeQuestionAnswers,
    initializePerGuestQuestionAnswers,
    upsertQuestionAnswer,
    upsertPerGuestQuestionAnswer,
    removePerGuestQuestionAnswersForTicket, // used by dispatch on other commands
    addFollowUpQuestions,
    addPerGuestFollowUpQuestions,
    removeFollowUpQuestionsAfter,
    removePerGuestFollowUpQuestionsAfter,
    cleanupDeletedAnswers,
    clearFollowUpTracking,
    clearAllAnswers,
    initializeFollowUpQuestionsFromDefaults,
    syncPerBookingChargesWithTicketCount
  }
}

declare global {
  export type CustomQuestionCommands = ReturnType<typeof customQuestion>

  // Add to global scope
  interface CommandRegistry {
    'custom-question': CustomQuestionCommands
  }
}
